/**
 * baseline JSON + lighthouse data → SummaryTabData 변환.
 *
 * 시안 (Claude Design 핸드오프) 의 root JSX 안 const DATA = {...} 객체와 1:1 호환.
 * Summary 탭은 root.jsx 가 window.__SUMMARY_DATA 로 읽음.
 */

import type { CodebaseReport, UIHealthConfig } from "../../types";
import { judge } from "../../utils/evaluate";
// 0.8.10 — FORBIDDEN 정의 단일 원천 (forbidden-meta.ts). 옛 로컬 복제 제거 —
//   code-tab.jsx 는 shell.ts 가 inject 하는 window.__FORBIDDEN_META 로 같은 정의 소비.
import { FORBIDDEN_BY_PRESET, FORBIDDEN_LABELS } from "../forbidden-meta";
import { deriveVariablesSignal } from "./figma-variables-signal";
import type { FigmaTabData, LighthouseTabData, SummaryTabData } from "./types";
// 0.8.10 — round() 공유 유틸로 이동 (옛 4곳 복제).
import { round } from "../../utils/round";

export function buildSummaryData(args: {
  report: CodebaseReport;
  lighthouse: LighthouseTabData | null;
  /** baseline.figma.warnings.length (시안의 figma.warningsCount). */
  figmaWarningsCount: number;
  /** figma transformer 결과 — primary / non-primary 라벨 정보. null = figma 누락. */
  figmaTabData?: FigmaTabData | null;
  /**
   * 0.8.8 — cfg.thresholds. 상태 배지 / 목표 표기를 markdown 리포터와 같은
   * evaluate() 판정으로 derive (옛 리터럴 배지 대체). 미전달 = 판정 null.
   */
  thresholds?: UIHealthConfig["thresholds"];
}): SummaryTabData {
  const { report, lighthouse, figmaWarningsCount, figmaTabData, thresholds } = args;
  const figmaReport = report.figma;

  const codeStamp = (report.generatedAt || "").slice(0, 10);
  const figmaStamp = (figmaReport?.generatedAt || "").slice(0, 10);
  const lhStamp = lighthouse?.measuredAt ?? "—";

  // 0.8.5 — preset 매트릭스 적용한 forbidden 배열 미리 build. JSX 는 단순 map 흐름.
  const preferredId = report.stylingMethodDistribution.preferredId;
  const forbiddenIds = (FORBIDDEN_BY_PRESET[preferredId] ?? []).map((s) => s.id);
  // 0.8.6 — 카운트 내림차순 sort. code 탭 ForbiddenSection / StylingMethodSection 정렬 흐름 일관.
  const forbiddenByPreset = forbiddenIds
    .map((id) => ({
      id,
      label: FORBIDDEN_LABELS[id] ?? id,
      value: report.forbiddenClassCount.byId[id] ?? 0,
    }))
    .sort((a, b) => b.value - a.value);

  // 0.8.8 — JS 잔여 상위 디렉토리 derive. 옛 시안 잔재 "(Top: apps/...)" 리터럴 대체.
  const tsTopJsDirs = [...(report.tsMigration.byDir ?? [])]
    .filter((r) => r.jsFiles > 0)
    .sort((a, b) => b.jsFiles - a.jsFiles)
    .slice(0, 3)
    .map((r) => r.dir);

  // ─── code 압축 ───
  const code: SummaryTabData["code"] = {
    scssCompliance: report.scssVariableCompliance.compliance,
    scssVariableUsages: report.scssVariableCompliance.variableUsages,
    scssHardcoded: report.hardcodedColors.total,
    forbiddenTotal: report.forbiddenClassCount.total,
    forbiddenByPreset,
    tsRatio: report.tsMigration.ratio,
    tsFiles: report.tsMigration.tsFiles,
    jsFiles: report.tsMigration.jsFiles,
    dsCoverage: report.dsCoverage.coverage,
    dsFilesUsing: report.dsCoverage.filesUsingDs,
    dsTotalConsumer: report.dsCoverage.totalConsumerFiles,
    migrationCandidateFiles: report.migrationCandidates.totalFilesAffected,
    migrationCandidateOccurrences: report.migrationCandidates.totalOccurrences,
    tsTopJsDirs,
    // 0.8.8 — markdown 리포터와 같은 evaluate() 판정 (utils/evaluate 공유).
    judge: {
      dsCoverage: judge(report.dsCoverage.coverage, thresholds?.dsCoverage),
      tsMigration: judge(report.tsMigration.ratio, thresholds?.tsMigration),
      forbidden: judge(
        report.forbiddenClassCount.total,
        thresholds?.forbiddenClassOccurrences
      ),
    },
  };

  // ─── lh 압축 ───
  let lh: SummaryTabData["lh"] = null;
  if (lighthouse) {
    const sortedByA11y = [...lighthouse.urls].sort((a, b) => a.a11y - b.a11y);
    const worst = sortedByA11y[0];

    // v0.9 note 13: cwvSample 이 배열 — Summary 탭의 1 target URL 강조 디자인 보존
    // 위해 perf 가장 낮은 URL 자동 선택 (worst URL). 매칭 LHR 없으면 fallback 으로
    // cwvSample[0] 사용. 빈 배열이면 target null.
    const sortedByPerf = [...lighthouse.urls].sort((a, b) => a.perf - b.perf);
    const worstPerfPath = sortedByPerf[0]?.path ?? null;
    const targetEntry =
      (worstPerfPath
        ? lighthouse.cwvSample.find((s) => s.url === worstPerfPath)
        : null) ?? lighthouse.cwvSample[0] ?? null;

    const target = targetEntry
      ? {
          url: targetEntry.url.replace(/^\//, ""),
          lcp: fmtTimeS(targetEntry.lcp),
          fcp: fmtTimeS(targetEntry.fcp),
          cls: round(targetEntry.cls, 3),
          tbt: fmtTimeMs(targetEntry.tbt),
          si: fmtTimeS(targetEntry.si),
          tti: fmtTimeS(targetEntry.tti),
        }
      : null;
    const targetPath = targetEntry
      ? "/" + targetEntry.url.replace(/^\//, "")
      : null;
    type LhRow = NonNullable<SummaryTabData["lh"]>["urlTable"][number];
    const urlTable = lighthouse.urls.map((u): LhRow => {
      if (targetPath && u.path === targetPath) {
        return [u.path, u.perf, u.a11y, u.bp, u.seo, true];
      }
      return [u.path, u.perf, u.a11y, u.bp, u.seo];
    });
    lh = {
      urls: lighthouse.totalUrls,
      runs: lighthouse.numberOfRuns,
      avgPerf: lighthouse.averages.perf,
      avgA11y: lighthouse.averages.a11y,
      avgBP: lighthouse.averages.bp,
      avgSeo: lighthouse.averages.seo,
      worst: worst ? { url: worst.path.replace(/^\//, ""), a11y: worst.a11y } : { url: "—", a11y: 0 },
      target,
      urlTable,
    };
  }

  // ─── figma 압축 (v0.12, Phase 0.6) — figmaReport 없으면 null 출력 ───
  // 다른 프로젝트 호환 핵심: figmaAnalysis=false 인 프로젝트는 figma 자체 null →
  // root.jsx 가 Summary Layer 03 + Figma 탭 hide.
  // 0.2.0: ds-new/ds-legacy hardcoded 형태 → primaryLabel + nonPrimaryLabels 필드로 변경.
  // dsNew/dsLegacy 변수 이름 = 옛 dashboard component 호환 위해 보존 (primary / 첫 non-primary 가리킴).
  let figma: SummaryTabData["figma"] = null;
  if (figmaReport) {
    const dsStats = figmaReport.tokenMatrix?.summary?.dsStats ?? {};
    const primaryLabel = figmaTabData?.primaryLabel ?? null;
    const nonPrimaryLabels = figmaTabData?.nonPrimaryLabels ?? [];
    const primary = primaryLabel ? dsStats[primaryLabel] : undefined;
    const firstNonPrimary = nonPrimaryLabels[0]
      ? dsStats[nonPrimaryLabels[0]]
      : undefined;
    // 0.8.8 — Variables 조회 신호 (옛 "plan 제약" 리터럴 대체 근거).
    const variablesSignal = deriveVariablesSignal(figmaReport);
    figma = {
      primaryLabel,
      nonPrimaryLabels,
      dsNewTotal: primary?.total ?? 0,
      dsNewMatched: primary?.matchedWithCode ?? 0,
      dsLegacyTotal: firstNonPrimary?.total ?? 0,
      dsLegacyMatched: firstNonPrimary?.matchedWithCode ?? 0,
      tokenRowsTotal: figmaReport.tokenMatrix?.summary?.totalUniqueTokens ?? 0,
      unmatchedInstances: figmaReport.instanceAnalysis?.unmatchedInstances ?? 0,
      totalInstances: figmaReport.instanceAnalysis?.totalInstances ?? 0,
      instanceSources: figmaReport.instanceSources ?? {},
      warningsCount: figmaWarningsCount,
      variablesRestricted: variablesSignal.restricted,
      variablesCount: variablesSignal.count,
    };
  }

  return {
    stamp: { code: codeStamp, figma: figmaStamp, lighthouse: lhStamp },
    code,
    lh,
    figma,
  };
}

function fmtTimeMs(v: number): string {
  if (v == null) return "—";
  if (v < 1000) return `${Math.round(v)} ms`;
  return `${(v / 1000).toFixed(2)} s`;
}

function fmtTimeS(v: number): string {
  if (v == null) return "—";
  if (v < 1000) return `${(v / 1000).toFixed(1)} s`;
  return `${(v / 1000).toFixed(2)} s`;
}

